if (!customElements.get('addon-price-calculator')) {
  customElements.define(
    'addon-price-calculator',
    class AddonPriceCalculator extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.currency = window?.Shopify?.currency?.active || this.dataset.currency || 'USD';
        this.locale = document.documentElement.lang || navigator.language || 'en-US';
        this.baseVariantPriceCents = 0;
        this.variantChangeUnsubscriber = undefined;
        this.boundMainQtyUpdate = this.update.bind(this);
        this.boundVariantIdChange = this.handleVariantIdChange.bind(this);
        this.priceObserver = null;
        this.priceObserverTimeout = null;
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.rows = Array.from(this.querySelectorAll('[data-addon-row]'));
        this.summaryBase = this.querySelector('[data-base-price]');
        this.summaryAddons = this.querySelector('[data-addon-price]');
        this.summaryEstimated = this.querySelector('[data-estimated-total]');
        this.hiddenSummary = this.querySelector('[data-addon-summary-input]');
        this.hiddenAddonTotal = this.querySelector('[data-addon-total-input]');

        this.mainQtyInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.productForm = this.productInfo?.querySelector(`#product-form-${this.sectionId}`) || null;
        this.variantIdInput = this.productForm?.querySelector('input[name="id"]') || null;

        this.hiddenBaseTotal =
          this.productInfo?.querySelector('[data-addon-base-total-input]') ||
          this.createHiddenInput('properties[_Base product total]', 'addon-base-total-input');

        this.hiddenEstimatedTotal =
          this.productInfo?.querySelector('[data-addon-estimated-total-input]') ||
          this.createHiddenInput('properties[_Estimated total]', 'addon-estimated-total-input');

        this.hiddenSelectedPackage =
          this.productInfo?.querySelector('[data-addon-selected-package-input]') ||
          this.createHiddenInput('properties[_Selected package]', 'addon-selected-package-input');

        this.bindRows();
        this.readCurrentVariantPrice();
        this.bindMainQuantity();
        this.bindVariantInput();
        this.observePriceBlock();

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event?.data) return;

            const eventSectionId = String(event.data.sectionId || '');
            if (eventSectionId !== String(this.sectionId)) return;

            const variant = event.data.variant;
            if (variant?.price != null) {
              this.baseVariantPriceCents = parseInt(variant.price, 10) || 0;
              this.updateSelectedPackage(variant);
              this.update();
            } else {
              this.syncVariantPriceFromDom();
              this.update();
            }
          });
        }

        this.update();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();

        if (this.mainQtyInput) {
          this.mainQtyInput.removeEventListener('input', this.boundMainQtyUpdate);
          this.mainQtyInput.removeEventListener('change', this.boundMainQtyUpdate);
        }

        if (this.variantIdInput) {
          this.variantIdInput.removeEventListener('change', this.boundVariantIdChange);
          this.variantIdInput.removeEventListener('input', this.boundVariantIdChange);
        }

        if (this.priceObserver) {
          this.priceObserver.disconnect();
          this.priceObserver = null;
        }

        if (this.priceObserverTimeout) {
          clearTimeout(this.priceObserverTimeout);
          this.priceObserverTimeout = null;
        }
      }

      createHiddenInput(name, dataKey) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-' + dataKey, '');
        input.setAttribute('form', `product-form-${this.sectionId}`);
        input.disabled = true;
        this.appendChild(input);
        return input;
      }

      bindMainQuantity() {
        if (!this.mainQtyInput) return;
        this.mainQtyInput.addEventListener('input', this.boundMainQtyUpdate);
        this.mainQtyInput.addEventListener('change', this.boundMainQtyUpdate);
      }

      bindVariantInput() {
        if (!this.variantIdInput) return;
        this.variantIdInput.addEventListener('change', this.boundVariantIdChange);
        this.variantIdInput.addEventListener('input', this.boundVariantIdChange);
      }

      handleVariantIdChange() {
        this.syncVariantPriceFromDom();
        this.update();
      }

      observePriceBlock() {
        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        if (!priceContainer || typeof MutationObserver === 'undefined') return;

        this.priceObserver = new MutationObserver(() => {
          if (this.priceObserverTimeout) clearTimeout(this.priceObserverTimeout);

          this.priceObserverTimeout = setTimeout(() => {
            this.syncVariantPriceFromDom();
            this.update();
          }, 30);
        });

        this.priceObserver.observe(priceContainer, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      bindRows() {
        this.rows.forEach((row) => {
          const checkbox = row.querySelector('[data-addon-checkbox]');
          const qtyInput = row.querySelector('[data-addon-qty]');
          const minusBtn = row.querySelector('[data-qty-minus]');
          const plusBtn = row.querySelector('[data-qty-plus]');

          if (!checkbox || !qtyInput) return;

          const toggleState = () => {
            row.classList.toggle('is-active', checkbox.checked);
            qtyInput.disabled = !checkbox.checked;

            if (minusBtn) minusBtn.disabled = !checkbox.checked;
            if (plusBtn) plusBtn.disabled = !checkbox.checked;

            this.update();
          };

          minusBtn?.addEventListener('click', () => {
            if (qtyInput.disabled) return;

            const step = this.getStep(qtyInput);
            const min = this.getMin(qtyInput);
            let value = this.getQtyValue(qtyInput) - step;

            if (value < min) value = min;

            qtyInput.value = value;
            this.update();
          });

          plusBtn?.addEventListener('click', () => {
            if (qtyInput.disabled) return;

            const step = this.getStep(qtyInput);
            const max = this.getMax(qtyInput);
            let value = this.getQtyValue(qtyInput) + step;

            if (max !== null && value > max) value = max;

            qtyInput.value = value;
            this.update();
          });

          qtyInput.addEventListener('input', () => {
            this.normalizeQtyInput(qtyInput);
            this.update();
          });

          qtyInput.addEventListener('change', () => {
            this.normalizeQtyInput(qtyInput);
            this.update();
          });

          checkbox.addEventListener('change', toggleState);
          toggleState();
        });
      }

      getCurrentVariantObject() {
        const selectedVariantScript = this.productInfo?.querySelector('variant-selects [data-selected-variant]');

        if (selectedVariantScript) {
          try {
            return JSON.parse(selectedVariantScript.textContent);
          } catch (error) {
            console.warn('Addon calculator: selected variant JSON parse failed.', error);
          }
        }

        return null;
      }

      readCurrentVariantPrice() {
        const variant = this.getCurrentVariantObject();

        if (variant?.price != null) {
          this.baseVariantPriceCents = parseInt(variant.price, 10) || 0;
          this.updateSelectedPackage(variant);
          return;
        }

        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        const salePrice = priceContainer?.querySelector('.price-item--sale.price-item--last');
        const regularPrices = priceContainer?.querySelectorAll('.price-item--regular');
        const regularPrice =
          regularPrices && regularPrices.length ? regularPrices[regularPrices.length - 1] : null;

        const sourceText = salePrice?.textContent?.trim() || regularPrice?.textContent?.trim() || '';
        this.baseVariantPriceCents = this.parseMoneyTextToCents(sourceText);

        if (this.hiddenSelectedPackage) {
          this.hiddenSelectedPackage.value = this.getSelectedVariantLabel();
          this.hiddenSelectedPackage.disabled = !this.hiddenSelectedPackage.value;
        }
      }

      syncVariantPriceFromDom() {
        this.readCurrentVariantPrice();
      }

      getSelectedVariantLabel() {
        const variant = this.getCurrentVariantObject();
        if (!variant) return '';

        if (variant.options && Array.isArray(variant.options) && variant.options.length) {
          return variant.options.join(' / ');
        }

        return variant.title && variant.title !== 'Default Title' ? variant.title : '';
      }

      updateSelectedPackage(variant) {
        if (!this.hiddenSelectedPackage) return;

        let value = '';

        if (variant?.options && Array.isArray(variant.options) && variant.options.length) {
          value = variant.options.join(' / ');
        } else if (variant?.title && variant.title !== 'Default Title') {
          value = variant.title;
        }

        this.hiddenSelectedPackage.value = value;
        this.hiddenSelectedPackage.disabled = !value;
      }

      getMainQuantity() {
        if (!this.mainQtyInput) return 1;

        const qty = parseInt(this.mainQtyInput.value || '1', 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 1;
      }

      getMin(input) {
        const min = parseInt(input.min || '0', 10);
        return Number.isFinite(min) ? min : 0;
      }

      getMax(input) {
        if (input.max === '' || input.max == null) return null;
        const max = parseInt(input.max, 10);
        return Number.isFinite(max) ? max : null;
      }

      getStep(input) {
        const step = parseInt(input.step || '1', 10);
        return Number.isFinite(step) && step > 0 ? step : 1;
      }

      getQtyValue(input) {
        const value = parseInt(input.value || '0', 10);
        return Number.isFinite(value) ? value : this.getMin(input);
      }

      normalizeQtyInput(input) {
        let value = this.getQtyValue(input);
        const min = this.getMin(input);
        const max = this.getMax(input);

        if (value < min) value = min;
        if (max !== null && value > max) value = max;

        input.value = value;
      }

      parseMoneyTextToCents(text) {
        if (!text) return 0;

        let cleaned = String(text).replace(/[^\d.,-]/g, '');

        if (cleaned.includes(',') && cleaned.includes('.')) {
          cleaned = cleaned.replace(/,/g, '');
        } else if (cleaned.includes(',') && !cleaned.includes('.')) {
          cleaned = cleaned.replace(',', '.');
        }

        const amount = parseFloat(cleaned);
        return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
      }

      formatMoneyFromCents(cents) {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format((cents || 0) / 100);
      }

      getRowUnitCents(row) {
        return this.parseMoneyTextToCents(row.dataset.unitPrice || '0');
      }

      isRowSelected(row) {
        const checkbox = row.querySelector('[data-addon-checkbox]');
        return !!checkbox?.checked;
      }

      getRowQuantity(row) {
        const qtyInput = row.querySelector('[data-addon-qty]');
        if (!qtyInput) return 0;

        this.normalizeQtyInput(qtyInput);
        return this.getQtyValue(qtyInput);
      }

      updateRow(row) {
        const selected = this.isRowSelected(row);
        const qty = this.getRowQuantity(row);
        const unitCents = this.getRowUnitCents(row);
        const lineCents = selected ? unitCents * qty : 0;

        const lineTotalEl = row.querySelector('[data-addon-line-total]');
        const propertyInput = row.querySelector('[data-addon-property]');
        const addonName = row.dataset.addonName || 'Add-on';

        if (lineTotalEl) {
          lineTotalEl.textContent = selected
            ? `${this.formatMoneyFromCents(unitCents)} × ${qty} = ${this.formatMoneyFromCents(lineCents)}`
            : `${this.formatMoneyFromCents(unitCents)} × 0 = ${this.formatMoneyFromCents(0)}`;
        }

        if (propertyInput) {
          if (selected && qty > 0) {
            propertyInput.disabled = false;
            propertyInput.value = `${addonName} | ${this.formatMoneyFromCents(unitCents)} × ${qty} = ${this.formatMoneyFromCents(lineCents)}`;
          } else {
            propertyInput.disabled = true;
            propertyInput.value = '';
          }
        }

        return {
          selected,
          name: addonName,
          qty,
          unitCents,
          lineCents,
        };
      }

      update() {
        this.syncVariantPriceFromDom();

        let addonsTotalCents = 0;
        const selectedLabels = [];

        this.rows.forEach((row) => {
          const rowResult = this.updateRow(row);
          addonsTotalCents += rowResult.lineCents;

          if (rowResult.selected && rowResult.qty > 0) {
            selectedLabels.push(
              `${rowResult.name} (${rowResult.qty}) = ${this.formatMoneyFromCents(rowResult.lineCents)}`
            );
          }
        });

        const mainQty = this.getMainQuantity();
        const baseTotalCents = this.baseVariantPriceCents * mainQty;
        const estimatedTotalCents = baseTotalCents + addonsTotalCents;

        if (this.summaryBase) {
          this.summaryBase.textContent = this.formatMoneyFromCents(baseTotalCents);
        }

        if (this.summaryAddons) {
          this.summaryAddons.textContent = this.formatMoneyFromCents(addonsTotalCents);
        }

        if (this.summaryEstimated) {
          this.summaryEstimated.textContent = this.formatMoneyFromCents(estimatedTotalCents);
        }

        if (this.hiddenSummary) {
          this.hiddenSummary.value = selectedLabels.join(' | ');
          this.hiddenSummary.disabled = selectedLabels.length === 0;
        }

        if (this.hiddenAddonTotal) {
          this.hiddenAddonTotal.value = this.formatMoneyFromCents(addonsTotalCents);
          this.hiddenAddonTotal.disabled = addonsTotalCents <= 0;
        }

        if (this.hiddenBaseTotal) {
          this.hiddenBaseTotal.value = this.formatMoneyFromCents(baseTotalCents);
          this.hiddenBaseTotal.disabled = baseTotalCents <= 0;
        }

        if (this.hiddenEstimatedTotal) {
          this.hiddenEstimatedTotal.value = this.formatMoneyFromCents(estimatedTotalCents);
          this.hiddenEstimatedTotal.disabled = estimatedTotalCents <= 0;
        }

        this.dataset.baseTotalCents = String(baseTotalCents);
        this.dataset.addonsTotalCents = String(addonsTotalCents);
        this.dataset.estimatedTotalCents = String(estimatedTotalCents);
        this.dataset.estimatedTotalMoney = this.formatMoneyFromCents(estimatedTotalCents);

        this.dispatchEvent(
          new CustomEvent('addon-calculator:updated', {
            bubbles: true,
            detail: {
              baseTotalCents,
              addonsTotalCents,
              estimatedTotalCents,
              estimatedTotalMoney: this.formatMoneyFromCents(estimatedTotalCents),
            },
          })
        );
      }
    }
  );
}